import moment from 'moment';

import {
  DownlaodIcon,
  NotificationIcon,
  TickCircleIcon,
  TrashIcon,
} from '../../../helpers/icons';
import { SEVERITY_TONE } from './constants';

// Square hit area for the row actions, so two icons of different intrinsic
// sizes still line up and stay comfortably clickable.
const ICON_BUTTON_CLASSES =
  'd--flex align-items--center justify-content--center w-min--24 w-max--24 h-min--24 h-max--24 radius--full bg--transparent c--pointer';

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = Number(bytes);
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return `${size.toFixed(size >= 10 || unit === 0 ? 0 : 1)} ${units[unit]}`;
};

/**
 * One row of the inbox, shared by the header panel and the full page so the
 * two never drift apart. `compact` is the header panel: same information,
 * tighter spacing and no row actions, since that list is a glance rather than
 * a workspace.
 */
const NotificationItem = ({
  notification,
  compact = false,
  isDownloading = false,
  onDownload,
  onOpen,
  onMarkRead,
  onDelete,
}) => {
  const download = notification?.download;
  const isBuilding = ['pending', 'in_progress'].includes(notification?.status);
  // While the archive is still being built, or while its link is being
  // fetched, the row is the only handle the user has on that job — deleting it
  // would lose the download with no way back to it. A failed job is not in
  // flight, so it stays deletable.
  const isDownloadInFlight = isBuilding || isDownloading;
  const tone = SEVERITY_TONE[notification?.severity] || SEVERITY_TONE.info;
  const isUnread = !notification?.is_read;

  const Icon =
    notification?.type === 'download'
      ? DownlaodIcon
      : notification?.severity === 'success'
        ? TickCircleIcon
        : NotificationIcon;

  return (
    <div
      className={`d--flex gap--sm p--sm border-bottom--black-100 ${
        isUnread ? 'bg--primary-50' : 'bg--white'
      }`}
    >
      {/* Fixed-width icon column so every title starts on the same x position,
          however long the row above it was. */}
      <div
        className={`w-min--36 w-max--36 h-min--36 h-max--36 radius--full d--flex align-items--center justify-content--center bg--${tone}-100 text--${tone}`}
      >
        <Icon width={18} height={18} />
      </div>

      <div className="w--full d--flex flex--column gap--xs">
        <div className="d--flex align-items--center justify-content--between gap--sm">
          <div className="d--flex align-items--center gap--xs">
            {isUnread && (
              <span className="w-min--7 w-max--7 h-min--7 h-max--7 radius--full bg--primary" />
            )}
            <span
              className={`font--sm font--600 text--black ${
                notification?.action_url ? 'c--pointer' : ''
              }`}
              onClick={() => onOpen?.(notification)}
            >
              {notification.title}
            </span>
          </div>

          <span className="font--xs text--gray white-space--nowrap">
            {compact
              ? moment(notification.created_at).fromNow()
              : moment(notification.created_at).format('DD MMM YYYY, HH:mm')}
          </span>
        </div>

        {notification.message && (
          <div className="font--2xs text--black-600">
            {notification.message}
          </div>
        )}

        {isBuilding && download && (
          <div className="d--flex flex--column gap--xs">
            <div className="font--xs text--gray">
              Preparing {download.processed_items} of {download.total_items} (
              {download.progress_percent}%)
            </div>
            {/* Track and fill, so a long-running job reads as progress rather
                than as a number that happens to change. */}
            <div className="w--full h-min--5 h-max--5 radius--full bg--black-100 overflow--hidden">
              <div
                className="h--full radius--full bg--primary"
                style={{ width: `${Math.max(2, download.progress_percent)}%` }}
              />
            </div>
          </div>
        )}

        <div className="d--flex align-items--center justify-content--between gap--sm">
          <div className="d--flex align-items--center gap--md">
            {download?.is_downloadable && (
              <button
                type="button"
                className="d--flex align-items--center gap--xs font--xs font--600 text--primary c--pointer bg--transparent"
                disabled={isDownloading}
                onClick={() => onDownload?.(notification)}
              >
                <DownlaodIcon width={14} height={14} />
                {isDownloading ? 'Fetching link...' : 'Download here'}
                {download.file_size ? (
                  <span className="text--gray font--400">
                    ({formatFileSize(download.file_size)})
                  </span>
                ) : null}
              </button>
            )}

            {/* A finished job whose archive has aged out still shows, so the
                user understands why the link is gone. */}
            {download &&
              !download.is_downloadable &&
              !isBuilding &&
              notification?.status === 'complete' && (
                <span className="font--xs text--gray">
                  This download has expired.
                </span>
              )}

            {!compact && notification.action_url && (
              <button
                type="button"
                className="font--xs font--600 text--primary c--pointer bg--transparent"
                onClick={() => onOpen?.(notification)}
              >
                Open
              </button>
            )}
          </div>

          {!compact && (
            <div className="d--flex align-items--center gap--xs white-space--nowrap">
              {/* Read is a state, not a destination, so the control disappears
                  once the row is read rather than turning into an "unread"
                  toggle nobody asked for. */}
              {isUnread && (
                <button
                  type="button"
                  data-tooltip="Mark as read"
                  tooltip-position="left"
                  className={ICON_BUTTON_CLASSES + ' text--primary'}
                  onClick={() => onMarkRead?.(notification)}
                >
                  <TickCircleIcon width={15} height={15} />
                </button>
              )}
              <button
                type="button"
                data-tooltip={
                  isDownloadInFlight
                    ? 'Download in progress'
                    : 'Delete'
                }
                tooltip-position="left"
                className={`${ICON_BUTTON_CLASSES} text--danger ${
                  isDownloadInFlight ? 'o--sm c--not-allowed' : ''
                }`}
                disabled={isDownloadInFlight}
                onClick={() => {
                  if (isDownloadInFlight) return;
                  onDelete?.(notification);
                }}
              >
                <TrashIcon width={14} height={14} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationItem;
